import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search,
  Download,
  Refresh,
  CheckCircle,
  Cancel,
  Pending,
  Receipt,
  CreditCard,
  AttachMoney,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from '../utils/axios';

const PaymentProcessing = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [paymentStats, setPaymentStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    successRate: 0,
    averageAmount: 0,
    pendingAmount: 0,
    refundedAmount: 0
  });

  useEffect(() => {
    fetchPayments();
    fetchPaymentStats();
  }, [page, rowsPerPage, statusFilter, dateRange, activeTab]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 0 ? '/api/payments' : 
                      activeTab === 1 ? '/api/payments/transactions' : 
                      '/api/payments/refunds';
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        days: dateRange
      };

      const response = await axios.get(endpoint, { params });
      
      if (activeTab === 0) {
        setPayments(response.data.payments);
      } else if (activeTab === 1) {
        setTransactions(response.data.transactions);
      } else {
        setRefunds(response.data.refunds);
      }
      
      setTotalItems(response.data.pagination.totalItems);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch payment data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentStats = async () => {
    try {
      const response = await axios.get('/api/payments/stats', {
        params: { days: dateRange }
      });
      setPaymentStats(response.data);
    } catch (err) {
      console.error('Failed to fetch payment stats:', err);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefund = async () => {
    try {
      await axios.post(`/api/payments/${selectedPayment._id}/refund`, {
        amount: parseFloat(refundAmount),
        reason: refundReason
      });
      setRefundDialog(false);
      setRefundAmount('');
      setRefundReason('');
      fetchPayments();
      fetchPaymentStats();
    } catch (err) {
      setError('Failed to process refund');
    }
  };

  const handleExport = async () => {
    try {
      const endpoint = activeTab === 0 ? '/api/payments/export' : 
                      activeTab === 1 ? '/api/payments/transactions/export' : 
                      '/api/payments/refunds/export';
                      
      const response = await axios.get(endpoint, {
        params: {
          status: statusFilter || undefined,
          days: dateRange
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      succeeded: { color: 'success', icon: <CheckCircle /> },
      pending: { color: 'warning', icon: <Pending /> },
      failed: { color: 'error', icon: <Cancel /> },
      refunded: { color: 'info', icon: <Receipt /> }
    };

    const config = statusConfig[status] || { color: 'default', icon: null };

    return (
      <Chip
        label={status.toUpperCase()}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const MetricCard = ({ title, value, icon, trend, color }) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h5" component="h2">
              {value}
            </Typography>
            {trend !== undefined && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend > 0 ? (
                  <TrendingUp style={{ color: '#4caf50', marginRight: 4 }} />
                ) : (
                  <TrendingDown style={{ color: '#f44336', marginRight: 4 }} />
                )}
                <Typography
                  variant="body2"
                  style={{ color: trend > 0 ? '#4caf50' : '#f44336' }}
                >
                  {Math.abs(trend)}% from last period
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            style={{
              backgroundColor: color || '#f5f5f5',
              borderRadius: '50%',
              padding: 12
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && !payments.length && !transactions.length && !refunds.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Payment Processing
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(paymentStats.totalRevenue)}
            icon={<AttachMoney style={{ color: '#fff' }} />}
            trend={paymentStats.revenueTrend}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Transactions"
            value={paymentStats.totalTransactions}
            icon={<CreditCard style={{ color: '#fff' }} />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Success Rate"
            value={`${paymentStats.successRate}%`}
            icon={<CheckCircle style={{ color: '#fff' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Average Amount"
            value={formatCurrency(paymentStats.averageAmount)}
            icon={<Receipt style={{ color: '#fff' }} />}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Pending"
            value={formatCurrency(paymentStats.pendingAmount)}
            icon={<Pending style={{ color: '#fff' }} />}
            color="#ff5722"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            title="Refunded"
            value={formatCurrency(paymentStats.refundedAmount)}
            icon={<Cancel style={{ color: '#fff' }} />}
            color="#607d8b"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="All Payments" />
          <Tab label="Transactions" />
          <Tab label="Refunds" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by ID, email, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="succeeded">Succeeded</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                  <MenuItem value="365">Last year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchPayments} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export to CSV">
                  <IconButton onClick={handleExport} color="primary">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Payments Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Payment ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(activeTab === 0 ? payments : activeTab === 1 ? transactions : refunds)
              .filter(payment => 
                !searchTerm || 
                payment._id.includes(searchTerm) ||
                payment.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.description?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((payment) => (
                <TableRow key={payment._id}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                      {payment._id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{payment.customerName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {payment.customerEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                  <TableCell>{getStatusChip(payment.status)}</TableCell>
                  <TableCell>
                    <Chip
                      label={payment.type}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{format(new Date(payment.createdAt), 'MMM dd, yyyy HH:mm')}</TableCell>
                  <TableCell>
                    {payment.status === 'succeeded' && activeTab === 0 && (
                      <Tooltip title="Process Refund">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setRefundDialog(true);
                          }}
                        >
                          <Receipt />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <Box>
              <Typography variant="body2" gutterBottom>
                Payment ID: {selectedPayment._id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Original Amount: {formatCurrency(selectedPayment.amount)}
              </Typography>
              <TextField
                fullWidth
                label="Refund Amount"
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                margin="normal"
                inputProps={{
                  max: selectedPayment.amount,
                  min: 0.01,
                  step: 0.01
                }}
              />
              <TextField
                fullWidth
                label="Refund Reason"
                multiline
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleRefund} 
            variant="contained" 
            color="primary"
            disabled={!refundAmount || !refundReason}
          >
            Process Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentProcessing;